import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { AlertTriangle, CheckCircle, Loader2 } from "lucide-react";
import { fetchProjectById, sendEmailToOwner } from "@/core/api/api";
import useToastStore from "@/shared/zustand/toastStore";
import useAuthStore from "@/shared/zustand/authStore";
import { generateAccessRequestEmailBody } from "@/shared/utils/appLayoutUtils/appLayoutUtilities";
const AccessDeniedDialog = ({ visible, onHide, projectName, }) => {
    const projectText = projectName ? `"${projectName}"` : "this project";
    const showToast = useToastStore((state) => state.showToast);
    const { user } = useAuthStore();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const requestAccess = async () => {
        setIsProcessing(true);
        try {
            const urlObject = new URL(window.location.href);
            const urlProjectId = urlObject.searchParams.get("projectId");
            if (urlProjectId) {
                const { responseData, error } = await fetchProjectById(urlProjectId);
                if (error) {
                    console.error("Error in retriving project details for request access", error);
                    showToast("error", "Error", "Error in retriving project details for request access");
                    return;
                }
                if (!user ||
                    !user.email ||
                    !user.provider.name ||
                    !user.provider.access_token ||
                    !user.provider.refresh_token) {
                    showToast("error", "Error", "User details insufficient to request access");
                    console.warn("User details insufficient to request access", user);
                    return;
                }
                if (responseData &&
                    "access_list" in responseData &&
                    responseData.access_list &&
                    Array.isArray(responseData.access_list) &&
                    responseData.access_list.length > 0) {
                    let ownerDetails = {
                        user_email: "",
                        user_name: "",
                        role: "",
                    };
                    const accessList = responseData.access_list;
                    accessList.forEach((access) => {
                        if (access.role === "owner") {
                            ownerDetails = access;
                        }
                    });
                    if (!ownerDetails.user_email || !ownerDetails.role) {
                        console.warn("Owner details missing for request access", accessList);
                        showToast("error", "Error", "Owner details missing for request access");
                        return;
                    }
                    const emailBody = generateAccessRequestEmailBody(user.provider.name, user.email, urlProjectId, responseData.name, window.location.origin, ownerDetails.user_email);
                    const emailSubject = "Request Access to " +
                        user.provider.name +
                        " for project " +
                        responseData.name;
                    const { responseData: emailResponseData, error: emailError } = await sendEmailToOwner(user?.email, ownerDetails.user_email, emailBody, emailSubject, user?.provider?.access_token, user?.provider?.refresh_token);
                    if (emailError) {
                        console.error("Error in sending request access email", emailError);
                        showToast("error", "Error", "Error in sending request access email");
                        return;
                    }
                    if (emailResponseData) {
                        console.log("Email sent successfully", emailResponseData);
                        showToast("success", "Success", "Email sent successfully");
                        setRequestSent(true);
                        return;
                    }
                }
                else {
                    console.warn("Invalid project details for request access", responseData);
                    showToast("error", "Error", "Invalid project details for request access");
                    return;
                }
            }
        }
        catch (error) {
            console.error("Unexpected error during access request:", error);
            showToast("error", "Error", "An unexpected error occurred while requesting access");
        }
        finally {
            setIsProcessing(false);
        }
    };
    const handleCancel = () => {
        setIsCancelling(true);
        // Add a small delay to show the loading state before redirecting
        setTimeout(() => {
            window.location.href = "/chat?newChat=true";
        }, 500);
    };
    const goToHome = () => {
        setIsCancelling(true);
        setTimeout(() => {
            window.location.href = "/chat?newChat=true";
        }, 500);
    };
    return (_jsx(Dialog, { visible: visible, onHide: onHide, dismissableMask: false, draggable: false, showHeader: false, className: "w-full max-w-lg rounded-lg shadow-lg border border-gray-200", contentClassName: "px-4 py-6 text-center", maskClassName: "!absolute !rounded-[8px] backdrop-blur-sm border border-[#d1d5db]", children: _jsx("div", { className: "flex flex-col items-center", children: requestSent ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "relative w-24 h-24 rounded-full bg-green-50 flex items-center justify-center mb-5", children: _jsx(CheckCircle, { size: 36, className: "text-green-500" }) }), _jsx("h2", { className: "text-2xl font-semibold text-gray-800 mb-3", children: "Request Sent Successfully" }), _jsxs("p", { className: "text-gray-600 mb-6 text-center max-w-md", children: ["Your access request for ", projectText, " has been sent to the owner. You'll be notified when access is granted."] }), _jsx("div", { className: "flex gap-2 w-full justify-center", children: _jsx(Button, { label: isCancelling ? "Redirecting..." : "Go to Home", icon: isCancelling ? (_jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })) : null, className: "py-2 px-8 bg-[#5928ED] hover:bg-[#4920CC] text-white rounded-[8px]", onClick: goToHome, disabled: isCancelling }) })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "relative w-24 h-24 rounded-full bg-red-50 flex items-center justify-center mb-5", children: _jsx(AlertTriangle, { size: 32, className: "text-red-500" }) }), _jsx("h2", { className: "text-2xl font-semibold text-gray-800 mb-3", children: "Access Denied" }), _jsxs("p", { className: "text-gray-600 mb-6 text-center max-w-md", children: ["Oops! It looks like you don't have access to ", projectText, ". You can request access or switch to an account that does."] }), _jsxs("div", { className: "flex gap-2 w-full justify-around", children: [_jsx(Button, { label: isCancelling ? "Redirecting..." : "Cancel", icon: isCancelling ? (_jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })) : null, className: "border border-[#D1D5DB] rounded-[8px] text-[#374151] py-2 px-8", onClick: handleCancel, disabled: isProcessing || isCancelling }), _jsx(Button, { label: isProcessing ? "Processing..." : "Request access", icon: isProcessing ? (_jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })) : null, className: "py-2 px-8 bg-[#5928ED] hover:bg-[#4920CC] text-white rounded-[8px]", onClick: requestAccess, disabled: isProcessing || isCancelling, loading: isProcessing })] })] })) }) }));
};
export default AccessDeniedDialog;
